import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PlayArrow,
  Stop,
  Schedule,
  Assessment,
  Warning,
} from '@mui/icons-material';

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 16px;
    padding: 1rem;
    max-width: 500px;
  }
`;

const SessionCard = styled(Box)`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1rem 0;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const ActionButton = styled(Button)`
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  text-transform: none;
`;

interface ActiveSession {
  sessionId: string;
  subject: string;
  grade: number;
  startTime: string;
  progress: {
    current: number;
    total: number;
    percentage: number;
  };
  score: number;
  currentQuestion?: any;
}

interface ActiveSessionDialogProps {
  open: boolean;
  session: ActiveSession | null;
  onResume: () => void;
  onEnd: (saveProgress: boolean) => void;
  onClose: () => void;
  loading?: boolean;
}

const ActiveSessionDialog: React.FC<ActiveSessionDialogProps> = ({
  open,
  session,
  onResume,
  onEnd,
  onClose,
  loading = false
}) => {
  const [saveProgress, setSaveProgress] = useState(true);

  if (!session) return null;

  const formatDuration = (startTime: string) => {
    const start = new Date(startTime);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - start.getTime()) / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return `${hours}h ${minutes}m ago`;
    }
  };

  const getSubjectColor = (subject: string) => {
    const colors = {
      math: '#2196f3',
      science: '#4caf50',
      english: '#ff9800',
      gk: '#9c27b0'
    };
    return colors[subject as keyof typeof colors] || '#757575';
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="warning" />
          <Typography variant="h6" fontWeight={600}>
            Active Test Session Found
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          You have an ongoing test session. You can resume where you left off or end the current session to start a new one.
        </Alert>

        <SessionCard>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip 
                label={session.subject.toUpperCase()} 
                sx={{ 
                  bgcolor: getSubjectColor(session.subject),
                  color: 'white',
                  fontWeight: 600
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Grade {session.grade}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
              <Schedule fontSize="small" />
              Started {formatDuration(session.startTime)}
            </Typography>
          </Box>

          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight={600}>
                Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {session.progress.current} / {session.progress.total} questions
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={session.progress.percentage} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                bgcolor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: getSubjectColor(session.subject)
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              {session.progress.percentage}% completed
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={0.5}>
              <Assessment fontSize="small" color="action" />
              <Typography variant="body2">
                Current Score: <strong>{session.score}%</strong>
              </Typography>
            </Box>
          </Box>
        </SessionCard>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            End Session Options
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={saveProgress}
                onChange={(e) => setSaveProgress(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                Save progress to my statistics
                <Typography variant="caption" display="block" color="text.secondary">
                  Your current score and time will be recorded
                </Typography>
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <ActionButton
          variant="outlined"
          onClick={() => onEnd(saveProgress)}
          disabled={loading}
          startIcon={<Stop />}
          color="error"
        >
          End Session
        </ActionButton>
        
        <ActionButton
          variant="contained"
          onClick={onResume}
          disabled={loading}
          startIcon={<PlayArrow />}
          sx={{
            background: `linear-gradient(135deg, ${getSubjectColor(session.subject)}, ${getSubjectColor(session.subject)}dd)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${getSubjectColor(session.subject)}dd, ${getSubjectColor(session.subject)}bb)`,
            }
          }}
        >
          Resume Test
        </ActionButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ActiveSessionDialog; 